import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { MessageRenderer, extractActionLinks } from "../../../../../components/MessageWithTableRenderer";
import { conversationTitle, formatDate, formatTime, formatTimestamp, printConversation } from "../exportConversation";

// Everything but this view is hidden from the printout. The page box carries
// the margins, so the view sets none of its own.
const PRINT_CSS = `@media print {
  @page { size: A4; margin: 16mm 14mm; }
  body > *:not(#conversation-print) { display: none !important; }
}`;

// The transcript laid out for paper: the details, the summary, then each
// message as the History page renders it. Never visible on screen: it mounts,
// opens the print dialog, and is removed once the dialog closes.
export function ConversationPrintView({ conv, messages, language, onDone }) {
  const zh = language === "zh";

  useEffect(() => {
    // A frame later, so the view has been laid out. Cancelled on unmount, so a
    // StrictMode remount does not open the dialog twice.
    const frame = requestAnimationFrame(() => printConversation(conv, messages, onDone));
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const title = conversationTitle(conv, messages) || (zh ? "未命名對話" : "Untitled conversation");
  const folder = conv?.folder?.trim();
  const details = [
    formatTimestamp(conv?.created_at, language),
    folder && `${zh ? "資料夾" : "Folder"}: ${folder}`,
    `${messages.length} ${zh ? "則訊息" : "messages"}`,
  ].filter(Boolean);
  const summary = conv?.summary?.text?.trim();

  // A date divider wherever the day changes, when there is more than one.
  const dates = messages.map((msg) => formatDate(msg.timestamp, language));
  const spansMultipleDays = new Set(dates.filter(Boolean)).size > 1;
  let previousDate = "";
  const startsDay = dates.map((date) => {
    const starts = spansMultipleDays && date && date !== previousDate;
    if (date) previousDate = date;
    return starts;
  });

  return createPortal(
    <div
      id="conversation-print"
      className="hidden print:block bg-white text-gray-900 font-sans"
      style={{ printColorAdjust: "exact", WebkitPrintColorAdjust: "exact" }}
    >
      <style>{PRINT_CSS}</style>

      <header className="pb-3 mb-5 border-b border-gray-300">
        <h1 className="text-xl font-bold break-words">{title}</h1>
        <p className="mt-1 text-sm text-gray-500">{details.join(" · ")}</p>
      </header>

      {summary && (
        <section className="mb-6 p-3 rounded-lg border border-gray-200 bg-gray-50 break-inside-avoid">
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
            {zh ? "摘要" : "Summary"}
          </h2>
          <p className="text-sm whitespace-pre-wrap break-words">{summary}</p>
        </section>
      )}

      {messages.map((msg, index) => {
        const isUser = msg.role === "user";
        const time = formatTime(msg.timestamp, language);
        // The renderer drops the source section, as it does on the page; on
        // paper there are no source buttons, so the links are listed instead.
        const sources = typeof msg.message === "string" ? extractActionLinks(msg.message) : [];
        return (
          <React.Fragment key={msg.id || index}>
            {startsDay[index] && (
              <div className="flex items-center gap-3 my-4 text-xs font-medium text-gray-400 break-after-avoid">
                <span className="h-px flex-1 bg-gray-200" />
                {dates[index]}
                <span className="h-px flex-1 bg-gray-200" />
              </div>
            )}
            <article className="mb-4 break-inside-avoid">
              <p className="mb-1 text-xs font-semibold text-gray-500">
                {isUser ? (zh ? "使用者" : "You") : zh ? "AI 代理人" : "AI Agent"}
                {time && ` · ${time}`}
              </p>
              <div
                className={`px-3 py-2 text-sm break-words border-l-4 ${
                  isUser ? "border-orange-400 bg-orange-50 rounded-r-lg" : "border-gray-200"
                }`}
                style={{ overflowWrap: "anywhere" }}
              >
                <MessageRenderer text={msg.message} language={language} hideActionLinks />
              </div>
              {sources.length > 0 && (
                <div className="mt-1.5 pl-4 text-xs text-gray-500">
                  <p className="font-medium">{zh ? "資料來源" : "Sources"}</p>
                  <ul className="mt-0.5 space-y-0.5">
                    {sources.map((link, i) => (
                      <li key={i} className="break-all">
                        {link.title}:{" "}
                        <a href={link.url} className="text-orange-600 underline">
                          {link.url}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </article>
          </React.Fragment>
        );
      })}
    </div>,
    document.body,
  );
}
